// ===============================
// AXOIX App Router
// Clean routes - no sample pages
// ===============================

import React from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import WowInit from "../../components/wowInit/wowInit";

// AXOIX Pages
import Homepage from "../HomePage";
import AboutPage from "../AboutPage/AboutPage";
import ServicePage from "../ServicePage/ServicePage";
import TeamPage from "../TeamPage/TeamPage";
import PricingPage from "../PricingPage/PricingPage";
import ContactPage from "../ContactPage/ContactPage";
import SignupPage from "../SignupPage/SignupPage";
import SignupResumePage from "../SignupResumePage/SignupResumePage";
import ErrorPage from "../ErrorPage/ErrorPage";
import ScrollToTop from "./ScrollToTop";
import RouteAnalytics from "./RouteAnalytics";

// Legal Pages
import TermsPage from "../TermsPage/TermsPage";
import PrivacyPage from "../PrivacyPage/PrivacyPage";
import CookiePolicyPage from "../CookiePolicyPage/CookiePolicyPage";
import RefundPage from "../RefundPage/RefundPage";
import DataDeletionPage from "../DataDeletionPage/DataDeletionPage";

// Content Pages
import FAQPage from "../FAQPage/FAQPage";
import CareersPage from "../CareersPage/CareersPage";
import BlogPage from "../BlogPage/BlogPage";
import BlogArticlePage from "../BlogArticlePage/BlogArticlePage";
import NewsletterConfirmedPage from "../NewsletterStatusPage/NewsletterConfirmedPage";
import NewsletterUnsubscribedPage from "../NewsletterStatusPage/NewsletterUnsubscribedPage";

// Partner Program Pages
import BecomePartner from "../PartnerPage/BecomePartner";
import PartnerLogin from "../PartnerPage/PartnerLogin";

// Checkout/Payment Page
import CheckoutPage from "../CheckoutPage/CheckoutPage";
import ShopCheckoutPage from "../ShopCheckoutPage/ShopCheckoutPage";

// Industry Pages
import IndustryDetailsPage from "../IndustryDetailsPage/IndustryDetailsPage";
import IndustriesPage from "../IndustriesPage/IndustriesPage";
import PGPage from "../PGPage/PGPage";
import DemoPage from "../DemoPage/DemoPage";

// Feature Pages
import FeaturesPage from "../FeaturesPage/FeaturesPage";
import FeatureDetailPage from "../FeatureDetailPage/FeatureDetailPage";
import InterconnectionsPage from "../InterconnectionsPage/InterconnectionsPage";

// Comparison Pages
import ComparePage from "../ComparePage/ComparePage";
import ComparisonPage from "../ComparisonPage/ComparisonPage";

// Router Component
const AllRoute: React.FC = () => {
  return (
    <>
      <WowInit />
      <ScrollToTop />
      <RouteAnalytics />

      <Routes>
          {/* Main Routes */}
          <Route path="/" element={<Homepage />} />
          <Route path="/home" element={<Homepage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/features" element={<FeaturesPage />} />
          <Route path="/feature/:slug" element={<FeatureDetailPage />} />
          <Route path="/how-it-connects" element={<InterconnectionsPage />} />

          {/* The Google integrations were briefly standalone pages at
              /integrations/*. They are feature modules now, like every other
              part of the product. These redirects exist because those URLs were
              live and are in Google's index — dropping them to the 404 handler
              would strand anyone following an old link. */}
          <Route path="/integrations" element={<Navigate to="/feature/google-workspace" replace />} />
          <Route path="/integrations/google" element={<Navigate to="/feature/google-workspace" replace />} />
          <Route path="/integrations/google-ads" element={<Navigate to="/feature/google-marketing" replace />} />
          <Route path="/google-workspace" element={<Navigate to="/feature/google-workspace" replace />} />
          <Route path="/google-ads" element={<Navigate to="/feature/google-marketing" replace />} />
          <Route path="/service" element={<ServicePage />} />
          {/* Theme-era link target that never had a route — it was referenced from
              the header mega-menu and the homepage, so every click rendered the 404
              page. Internal links now point at /service; this keeps old external
              links and bookmarks alive. */}
          <Route path="/service-details" element={<Navigate to="/service" replace />} />
          <Route path="/team" element={<TeamPage />} />
          <Route path="/pricing" element={<PricingPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/signup" element={<SignupPage />} />
          <Route path="/register" element={<SignupPage />} />
          <Route path="/start-free-trial" element={<SignupPage />} />
          <Route path="/checkout" element={<CheckoutPage />} />
          {/* Payment page for a tenant who ALREADY exists, arriving from their
              own *.axoix.io dashboard with a single-use session token. Separate
              from /checkout, which is the signup flow and bounces to /signup
              when it finds no in-app state. See BR-115/BR-116. */}
          <Route path="/shop-checkout" element={<ShopCheckoutPage />} />
          <Route path="/signup/resume" element={<SignupResumePage />} />
          <Route path="/industries" element={<IndustriesPage />} />
          <Route path="/industry/pg-hostel-management-software" element={<PGPage />} />
          <Route path="/industry/:slug" element={<IndustryDetailsPage />} />
          {/* Legacy route — redirect to canonical /industry/pg-hostel-management-software */}
          <Route path="/pg-hostel-management-software" element={<Navigate to="/industry/pg-hostel-management-software" replace />} />
          <Route path="/compare" element={<ComparePage />} />
          <Route path="/compare/:slug" element={<ComparisonPage />} />
          <Route path="/demo/:vertical" element={<DemoPage />} />

          {/* Legal Pages */}
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/cookies" element={<CookiePolicyPage />} />
          <Route path="/refund" element={<RefundPage />} />
          <Route path="/data-deletion" element={<DataDeletionPage />} />

          {/* Content Pages */}
          <Route path="/faq" element={<FAQPage />} />
          <Route path="/careers" element={<CareersPage />} />
          <Route path="/blog" element={<BlogPage />} />
          <Route path="/blog/:slug" element={<BlogArticlePage />} />
          <Route path="/newsletter/confirmed" element={<NewsletterConfirmedPage />} />
          <Route path="/newsletter/unsubscribed" element={<NewsletterUnsubscribedPage />} />

          {/* Partner Program */}
          <Route path="/become-partner" element={<BecomePartner />} />
          <Route path="/partner" element={<PartnerLogin />} />
          <Route path="/partner/login" element={<PartnerLogin />} />
          <Route path="/partners" element={<BecomePartner />} />
          <Route path="/affiliate" element={<BecomePartner />} />

          {/* Error Page */}
          <Route path="/404" element={<ErrorPage />} />
          <Route path="*" element={<ErrorPage />} />
      </Routes>
    </>
  );
};

export default AllRoute;
